import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import PendingCard from '@/components/Dashboard/DashboardPendings/PendingsCard';
import OtherTooltip from '../../OtherTooltip';
import OverviewLeadRow from './OverviewLeadRow';
import useFetchDashboard from '@/components/Dashboard/customHooks/useFetchDashboard';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import styles from './overviewLeads.module.scss';
import {
  AGENT,
  BRANCH,
} from '@/components/FilterBar/constants/filtersConstants';
import { getOverviewLeads } from '@/store/slices/components/dashboard/service';
import DashboardEmptyStateCard from '@/components/Dashboard/DashboardEmptyStateCard';
import InquiryIcon from '@/public/images/dashboard/inquiry.svg';
import { useTrackOverviewTab } from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function OverviewLeads() {
  const dispatch = useDispatch();
  const { overviewLeads, filters } = useSelector(
    (state) => state.DashboardSlice
  );
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const track = useTrackOverviewTab();
  const tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost');
  const { list, others, loading, hasFetched, empty } = overviewLeads;

  useFetchOnViewport(
    (query) => {
      dispatch(getOverviewLeads(query));
    },
    [],
    { hasFetched }
  );

  const othersIds = others.map((event) => event.id);
  const othersContacts = others.reduce((acc, curr) => acc + curr.contacts, 0);

  const getBaseUrl = () => {
    let url = `${tokkoBrokerHost}/leads/?only_with_perm=true`;
    let withNoParams = true;

    if (filters.filterBy === AGENT && filters.selectedIds.length > 0) {
      url += `&agent=[${filters.selectedIds}]`;
      withNoParams = false;
    }
    if (filters.filterBy === BRANCH && filters.selectedIds.length > 0) {
      url += `&branch_ids=[${filters.selectedIds}]`;
      withNoParams = false;
    }

    if (withNoParams) {
      url += '&company_leads=true';
    }

    return url;
  };

  const openLeadsView = (idToRedirect) => {
    let url = getBaseUrl();
    url += `&lead_status_ids=[${idToRedirect}]`;

    window.open(url, '_blank');
  };

  const getTooltipContent = () => {
    const maxVisible = 12;
    const visible = others.slice(0, maxVisible);
    const remaining = others.length - maxVisible;

    return (
      <>
        {visible.map((lead) => (
          <Text key={lead.id}>{`${lead.name}: ${lead.contacts}`}</Text>
        ))}
        {remaining > 0 && (
          <Text>{`${Trans(translations, 'CountMore', remaining)}...`}</Text>
        )}
      </>
    );
  };

  return (
    <PendingCard ref={ref} loading={loading || isStale}>
      <PendingCard.Header
        redirectUrl={!empty && getBaseUrl()}
        toolTipText={Trans(translations, 'ShowAll')}
        trackCallback={(element) => track.leads(element)}
      >
        {Trans(translations, 'Leads')}
      </PendingCard.Header>
      <Box className={styles.overviewLeads}>
        {empty ? (
          <DashboardEmptyStateCard
            icon={InquiryIcon}
            title={Trans(translations, 'HereYouWillSeeHowManyLeadsYouHave')}
          />
        ) : (
          <>
            {list.map((lead) => (
              <OverviewLeadRow
                key={lead.id}
                stats={{
                  text: lead.name,
                  backgroundColor: lead.color,
                  contacts: lead.contacts,
                }}
                onClick={() => {
                  openLeadsView(lead.id);
                  track.leads(TRACK_ELEMENT.ROW);
                }}
                data-testid={`leads-row-${lead.id}`}
              />
            ))}

            {others.length > 0 && (
              <Box className={styles.othersRow}>
                <OverviewLeadRow
                  stats={{
                    text: Trans(translations, 'OtherLeads'),
                    backgroundColor: null,
                    contacts: othersContacts,
                  }}
                  onClick={() => {
                    openLeadsView(othersIds);
                    track.leads(TRACK_ELEMENT.ROW);
                  }}
                  data-testid="leads-row-others"
                />

                <OtherTooltip className={styles.tooltip}>
                  {getTooltipContent()}
                </OtherTooltip>
              </Box>
            )}
          </>
        )}
      </Box>
    </PendingCard>
  );
}
